import React, { useState, useEffect } from "react";
import "./Coursefaculty.css";
import ActionBar from "../Components/BottomActionBar/ActionBar";
import UpdateComponent from "../Components/UpdateComponent/UpdateComponent";
import { courseFacultyAPI, funcAreaAPI, facultyAPI } from "./api";
import DataTable from "./DataTable";
import FacultyViewer from "../Components/Viewers/FacultyViewer";

function Coursefaculty() {
  const [facultyName, setFacultyName] = useState("");
  const [facultyCode, setFacultyCode] = useState("");
  const [funcSubAreaCode, setFuncSubAreaCode] = useState("");
  const [funcAreaOptions, setFuncAreaOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState("");

  const [showFacViewer, setShowFacViewer] = useState(false);
  const [facultyList, setFacultyList] = useState([]);

  useEffect(() => {
    loadFuncAreas();
    loadFacultyList();
  }, []);

  // const loadFuncAreas = async () => {
  //   try {
  //     const data = await funcAreaAPI.getAll();
  //     setFuncAreaOptions(data);
  //   } catch (error) {
  //     // skip
  //   }
  // };
  
const loadFuncAreas = async () => {
  try {
    const data = await funcAreaAPI.getAll();
    const safe = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    setFuncAreaOptions(safe);
  } catch (error) {
    setFuncAreaOptions([]);
  }
};


  const loadFacultyList = async () => {
    try {
      const data = await facultyAPI.getAll();
      setFacultyList(Array.isArray(data) ? data : []);
    } catch { /* skip */ }
  };

  const getFacultyName = (code) => {
    const fac = facultyList.find((f) => String(f.faculty_code) === String(code));
    return fac ? (fac.faculty_name ?? code) : code;
  };

  const clearMessages = () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const buildParams = () => {
    const params = {};
    if (facultyCode) params.faculty_code = facultyCode;
    if (funcSubAreaCode.trim()) params.func_sub_area_code = funcSubAreaCode.trim();
    return params;
  };

  const handleInsert = async () => {
    setUpdateMsg(null);
    setDeleteMsg(null);

    if (!facultyCode || !funcSubAreaCode.trim()) {
      alert("Faculty Name and Functional Area are required fields.");
      return;
    }

    try {
      const res = await courseFacultyAPI.create({
        faculty_code: Number(facultyCode),
        func_sub_area_code: funcSubAreaCode.trim(),
      });
      if (res.ok) {
        setInsertMsg({ text: "Record inserted successfully.", success: true });
        const data = await courseFacultyAPI.getAll(buildParams()).catch(() => []);
        setTableData(Array.isArray(data) ? data : []);
      }
      else setInsertMsg({ text: "Insert failed.", success: false });
    } catch {
      setInsertMsg({ text: "Insert failed.", success: false });
    }
  };

  const handleUpdate = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(true);
    setEditRows({});

    try {
      const data = await courseFacultyAPI.getAll(buildParams());
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const handleView = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(false);
    setEditRows({});

    try {
      const data = await courseFacultyAPI.getAll(buildParams());
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const toggleEditRow = (idx, row) => {
    setEditRows((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return {
        ...prev,
        [idx]: {
          faculty_code: row.faculty_code ?? "",
          func_sub_area_code: row.func_sub_area_code ?? "",
        },
      };
    });

    setSelectedForUpdate((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const selectedIndexes = Object.keys(selectedForUpdate).filter((i) => selectedForUpdate[i]);

    if (selectedIndexes.length === 0) {
      setUpdateMsg({ text: "No records selected for update.", success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      const editedData = editRows[idx];
      if (!editedData) continue;

      try {
        const res = await courseFacultyAPI.update(row.faculty_code, row.func_sub_area_code, {
          func_sub_area_code: editedData.func_sub_area_code,
        });

        if (res.ok) successCount++;
        else {
          await res.json().catch(() => ({}));
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setUpdateMsg({ text: `${successCount} record(s) updated successfully.`, success: true });
      const data = await courseFacultyAPI.getAll(buildParams()).catch(() => []);
      setTableData(Array.isArray(data) ? data : []);
    }
    if (failCount > 0) {
      setUpdateMsg({ text: `${failCount} record(s) failed to update.`, success: false });
    }

    setSelectedForUpdate({});
    setEditRows({});
  };

  const handleDeleteSelected = async () => {
    const selectedIndexes = Object.keys(selectedForDelete).filter((i) => selectedForDelete[i]);

    if (selectedIndexes.length === 0) {
      setDeleteMsg({ text: "No records selected for delete.", success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      try {
        const res = await courseFacultyAPI.delete(row.faculty_code, row.func_sub_area_code);
        if (res.ok) successCount++;
        else {
          await res.json().catch(() => ({}));
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setDeleteMsg({ text: `${successCount} record(s) deleted successfully.`, success: true });
      const data = await courseFacultyAPI.getAll(buildParams()).catch(() => []);
      setTableData(Array.isArray(data) ? data : []);
    }
    if (failCount > 0) {
      setDeleteMsg({ text: `${failCount} record(s) failed to delete.`, success: false });
    }

    setSelectedForDelete({});
  };

  const handleReset = () => {
    setFacultyName("");
    setFacultyCode("");
    setFuncSubAreaCode("");
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setTableData([]);
    setShowTable(false);
    setSortBy("");
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sorted = [...data];
    switch (sortOption) {
      case "Faculty Name":
        return sorted.sort((a, b) => String(getFacultyName(a.faculty_code) || "").localeCompare(String(getFacultyName(b.faculty_code) || "")));
      case "Functional Area":
        return sorted.sort((a, b) => String(a.func_sub_area_code || "").localeCompare(String(b.func_sub_area_code || "")));
      default:
        return sorted;
    }
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) {
      setTableData(sortTableData(tableData, sortOption));
    }
  };

  const handleFacultySelect = (row) => {
    setFacultyName(row.faculty_name ?? "");
    setFacultyCode(row.faculty_code ?? "");
    setShowFacViewer(false);
  };

  const handleClear = () => {
    setFacultyName("");
    setFacultyCode("");
  };

  return (
    <div className="cfBody" onClick={clearMessages}>
      <form onSubmit={(e) => e.preventDefault()}>
        <table className="cfFormTable">
          <tbody>
            <tr>
              <td className="cfTdLabel">
                Faculty Name<span className="cfImportant">*</span>
              </td>
              <td className="cfTdField">
                <input
                  className="cfFacultyInput"
                  type="text"
                  value={facultyName}
                  readOnly
                  onFocus={(e) => e.target.blur()}
                />
              </td>
              <td className="cfTdField">
                <button type="button" className="cfBtnPrimary" onClick={() => setShowFacViewer(true)}>
                  Get Faculty
                </button>
                <button type="button" className="cfBtnSecondary" onClick={handleClear}>
                  Clear
                </button>
              </td>
            </tr>
            <tr>
              <td className="cfTdLabel">
                Functional Area<span className="cfImportant">*</span>
              </td>
              <td className="cfTdField" colSpan={2}>
                <select
                  className="cfFuncAreaSelect"
                  value={funcSubAreaCode}
                  onChange={(e) => setFuncSubAreaCode(e.target.value)}
                  onFocus={clearMessages}
                >
                  <option value="">Select Functional Area</option>
                  {funcAreaOptions.map((area, idx) => (
                    <option key={idx} value={area.func_sub_area_code}>
                      {area.func_sub_area_name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td colSpan={3}>
                <p className="cfRemarks">
                  <span className="cfImportant">*</span> indicates a required field for 'INSERT' &amp; 'UPDATE'.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <ActionBar
        sortOptions={["Faculty Name", "Functional Area"]}
        onView={handleView}
        onReset={handleReset}
        onInsert={handleInsert}
        onUpdate={handleUpdate}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />

      {insertMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: insertMsg.success ? "green" : "red", margin: "4px 0" }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: updateMsg.success ? "green" : "red", margin: "4px 0" }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: deleteMsg.success ? "green" : "red", margin: "4px 0" }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={[
            { header: "Faculty Name", key: "faculty_code", accessor: (row) => getFacultyName(row.faculty_code) },
            {
              header: "Functional Area",
              key: "func_sub_area_code",
              accessor: (row) => {
                const area = funcAreaOptions.find((a) => a.func_sub_area_code === row.func_sub_area_code);
                return area ? `${area.func_sub_area_name} [${row.func_sub_area_code}]` : row.func_sub_area_code ?? "";
              },
              editable: true,
              renderEdit: (idx, editRows, onChangeEditCell) => (
                <select
                  className="cfFuncAreaSelect"
                  value={editRows[idx]?.func_sub_area_code ?? ""}
                  onChange={(e) => onChangeEditCell(idx, "func_sub_area_code", e.target.value)}
                >
                  <option value="">Select Functional Area</option>
                  {funcAreaOptions.map((area, i) => (
                    <option key={i} value={area.func_sub_area_code}>
                      {area.func_sub_area_name}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) =>
            setEditRows((prev) => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))
          }
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          emptyMessage="No data found"
          rowKey={(row, idx) => `${row.faculty_code}-${row.func_sub_area_code}-${idx}`}
        />
      )}

      {showTable && showEditCols && (
        <UpdateComponent onUpdateSelected={handleUpdateSelected} onDeleteSelected={handleDeleteSelected} />
      )}

      {showFacViewer && (
        <div className="facviewer-overlay" onClick={() => setShowFacViewer(false)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setShowFacViewer(false)}>&times;</button>
            <FacultyViewer selectable onSelect={handleFacultySelect} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Coursefaculty;
